import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Flame, Calendar, Layers, Package, Plus, Trash2, 
  Clock, Thermometer, CheckSquare, FileText, History, User, Check, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parse } from 'date-fns';
import { cs } from 'date-fns/locale';

const AutoclaveDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [record, setRecord] = useState(null);
  const [processFieldTypes, setProcessFieldTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [foodDialogOpen, setFoodDialogOpen] = useState(false);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [foodForm, setFoodForm] = useState({ food_name: '', quantity: 1, batch: '' });
  const [processForm, setProcessForm] = useState({ field_type_id: '', value: '' });
  const [selectedFieldType, setSelectedFieldType] = useState(null);

  const fetchRecord = useCallback(async () => {
    try {
      const [recordRes, typesRes] = await Promise.all([
        api.get(`/autoclave/records/${id}`),
        api.get('/autoclave/process-field-types')
      ]);
      setRecord(recordRes.data);
      setProcessFieldTypes(typesRes.data);
    } catch (error) {
      toast.error('Chyba při načítání záznamu');
      navigate('/autoklav');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchRecord();
  }, [fetchRecord]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = parse(dateStr, 'yyyy-MM-dd', new Date());
      return format(date, 'd. MMMM yyyy', { locale: cs });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return format(date, 'd. M. yyyy HH:mm', { locale: cs });
    } catch {
      return dateStr;
    }
  };

  // Food items
  const handleAddFood = async () => {
    if (!foodForm.food_name || !foodForm.batch) {
      toast.error('Vyplňte název jídla a šarži');
      return;
    }
    try {
      await api.post(`/autoclave/records/${id}/food-items`, foodForm);
      toast.success('Jídlo přidáno');
      setFoodDialogOpen(false);
      setFoodForm({ food_name: '', quantity: 1, batch: '' });
      fetchRecord();
    } catch (error) {
      toast.error('Chyba při přidávání jídla');
    }
  };

  const handleDeleteFood = async () => {
    if (!itemToDelete) return;
    try {
      await api.delete(`/autoclave/records/${id}/food-items/${itemToDelete.id}`);
      toast.success('Jídlo odebráno');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchRecord();
    } catch (error) {
      toast.error('Chyba při mazání');
    }
  };

  // Process entries
  const handleOpenProcessDialog = (fieldType) => {
    setSelectedFieldType(fieldType);
    let defaultValue = '';
    if (fieldType.field_type === 'time') {
      defaultValue = format(new Date(), 'HH:mm');
    } else if (fieldType.field_type === 'checkbox') {
      defaultValue = false;
    } else if (fieldType.field_type === 'temperature') {
      defaultValue = '';
    }
    setProcessForm({ field_type_id: fieldType.id, value: defaultValue });
    setProcessDialogOpen(true);
  };

  const handleAddProcessEntry = async () => {
    if (!selectedFieldType) return;
    
    try {
      await api.post(`/autoclave/records/${id}/process-entries`, {
        field_type_id: selectedFieldType.id,
        field_name: selectedFieldType.name,
        field_type: selectedFieldType.field_type,
        value: processForm.value
      });
      toast.success('Záznam přidán');
      setProcessDialogOpen(false);
      fetchRecord();
    } catch (error) {
      toast.error('Chyba při přidávání záznamu');
    }
  };

  const handleDeleteProcessEntry = async (entryId) => {
    try {
      await api.delete(`/autoclave/records/${id}/process-entries/${entryId}`);
      toast.success('Záznam odebrán');
      fetchRecord();
    } catch (error) {
      toast.error('Chyba při mazání záznamu');
    }
  };

  const handleCompleteRecord = async () => {
    try {
      await api.put(`/autoclave/records/${id}/status`, { status: 'completed' });
      toast.success('Záznam dokončen');
      fetchRecord();
    } catch (error) {
      toast.error('Chyba při dokončování záznamu');
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await api.get(`/autoclave/records/${id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `autoklav_${record.device_number}_${record.date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exportováno');
    } catch (error) {
      toast.error('Chyba při exportu PDF');
    }
  };

  const getFieldTypeIcon = (type) => {
    switch (type) {
      case 'time': return <Clock className="h-4 w-4" />;
      case 'temperature': return <Thermometer className="h-4 w-4" />;
      case 'checkbox': return <CheckSquare className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const formatProcessValue = (entry) => {
    if (entry.field_type === 'checkbox') {
      return entry.value ? 'Ano' : 'Ne';
    }
    if (entry.field_type === 'temperature') {
      return `${entry.value} °C`;
    }
    return entry.value;
  };

  // Check which process field types are already recorded
  const getRecordedFieldTypeIds = () => {
    return new Set((record?.process_entries || []).map(e => e.field_type_id));
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Načítání...</div>
      </Layout>
    );
  }

  if (!record) {
    return null;
  }

  const recordedIds = getRecordedFieldTypeIds();

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-sm font-mono rounded font-semibold">
                  #{record.record_number || '-'}
                </span>
                <h1 className="text-2xl font-bold text-slate-900">
                  {record.device_number} - {record.device_name}
                </h1>
                <Badge variant="secondary" className={
                  record.status === 'completed' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-amber-100 text-amber-700'
                }>
                  {record.status === 'completed' ? 'Dokončeno' : 'Probíhá'}
                </Badge>
              </div>
              <p className="text-sm text-slate-500">
                {formatDate(record.date)}
                {record.created_at && (
                  <span className="ml-2">
                    • Vytvořeno: {format(new Date(record.created_at), 'HH:mm', { locale: cs })}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleExportPDF} variant="outline" className="border-teal-200 text-teal-700 hover:bg-teal-50">
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
            {record.status !== 'completed' && (
              <Button onClick={handleCompleteRecord} className="bg-green-600 hover:bg-green-700">
                <Check className="h-4 w-4 mr-2" />
                Dokončit záznam
              </Button>
            )}
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Flame className="h-5 w-5 text-teal-600" />
            Základní informace
          </h2>
          <div className="grid sm:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-slate-500">ID záznamu</p>
              <p className="font-medium text-slate-900 font-mono text-lg">#{record.record_number || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Datum</p>
              <p className="font-medium text-slate-900 flex items-center gap-1">
                <Calendar className="h-4 w-4 text-slate-400" />
                {formatDate(record.date)}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Autokláv</p>
              <p className="font-medium text-slate-900">{record.device_number} - {record.device_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Program</p>
              <p className="font-medium text-slate-900 flex items-center gap-1">
                <Layers className="h-4 w-4 text-slate-400" />
                {record.program_number} - {record.program_name}
              </p>
            </div>
          </div>
          {record.created_at && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-500">
                Vytvořeno: {format(new Date(record.created_at), "d. MMMM yyyy 'v' HH:mm", { locale: cs })}
                {record.created_by_name && ` • ${record.created_by_name}`}
              </p>
            </div>
          )}
        </div>

        {/* Food Items */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <Package className="h-5 w-5 text-orange-600" />
              Jídla ({record.food_items?.length || 0})
            </h2>
            {record.status !== 'completed' && (
              <Button size="sm" onClick={() => setFoodDialogOpen(true)} className="bg-orange-600 hover:bg-orange-700">
                <Plus className="h-4 w-4 mr-1" />
                Přidat jídlo
              </Button>
            )}
          </div>
          {record.food_items?.length === 0 ? (
            <p className="text-slate-500 text-sm">Zatím nebyla přidána žádná jídla</p>
          ) : (
            <div className="space-y-2">
              {record.food_items.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">{item.food_name}</p>
                    <p className="text-sm text-slate-500">
                      {item.quantity} ks • Šarže: {item.batch}
                    </p>
                  </div>
                  {record.status !== 'completed' && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-600 hover:text-red-700"
                      onClick={() => { setItemToDelete(item); setDeleteDialogOpen(true); }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Process Evidence */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            Evidence procesu
          </h2>
          
          {/* Available process fields to record */}
          {record.status !== 'completed' && (
            <div className="mb-6">
              <p className="text-sm text-slate-500 mb-3">Zaznamenat:</p>
              <div className="flex flex-wrap gap-2">
                {processFieldTypes.map((fieldType) => {
                  const isRecorded = recordedIds.has(fieldType.id);
                  return (
                    <Button
                      key={fieldType.id}
                      variant={isRecorded ? "secondary" : "outline"}
                      size="sm"
                      onClick={() => !isRecorded && handleOpenProcessDialog(fieldType)}
                      disabled={isRecorded}
                      className={isRecorded ? 'bg-green-100 text-green-700' : ''}
                    >
                      {getFieldTypeIcon(fieldType.field_type)}
                      <span className="ml-1">{fieldType.name}</span>
                      {isRecorded && <Check className="h-3 w-3 ml-1" />}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recorded entries */}
          {record.process_entries?.length === 0 ? (
            <p className="text-slate-500 text-sm">Zatím nebyly zaznamenány žádné údaje procesu</p>
          ) : (
            <div className="space-y-2">
              {record.process_entries.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      entry.field_type === 'checkbox' && entry.value 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-blue-100 text-blue-600'
                    }`}>
                      {getFieldTypeIcon(entry.field_type)}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{entry.field_name}</p>
                      <p className="text-sm text-slate-500">
                        {formatProcessValue(entry)} • {entry.recorded_by_name} • {formatDateTime(entry.recorded_at)}
                      </p>
                    </div>
                  </div>
                  {record.status !== 'completed' && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDeleteProcessEntry(entry.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* History */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <History className="h-5 w-5 text-purple-600" />
            Historie změn
          </h2>
          {record.history?.length === 0 ? (
            <p className="text-slate-500 text-sm">Žádná historie</p>
          ) : (
            <div className="space-y-3">
              {[...record.history].reverse().map((entry) => (
                <div key={entry.id} className="flex gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-slate-900">
                      <span className="font-medium">{entry.user_name}</span>
                      {' - '}
                      {entry.description}
                    </p>
                    <p className="text-slate-400">{formatDateTime(entry.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Food Dialog */}
      <Dialog open={foodDialogOpen} onOpenChange={setFoodDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Přidat jídlo</DialogTitle>
            <DialogDescription>Zadejte informace o jídle</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Název jídla *</Label>
              <Input
                value={foodForm.food_name}
                onChange={(e) => setFoodForm({ ...foodForm, food_name: e.target.value })}
                placeholder="např. Hovězí guláš"
              />
            </div>
            <div className="space-y-2">
              <Label>Počet kusů *</Label>
              <Input
                type="number"
                min="1"
                value={foodForm.quantity}
                onChange={(e) => setFoodForm({ ...foodForm, quantity: parseInt(e.target.value) || 1 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Šarže *</Label>
              <Input
                value={foodForm.batch}
                onChange={(e) => setFoodForm({ ...foodForm, batch: e.target.value })}
                placeholder="např. 2026-03-24-A"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFoodDialogOpen(false)}>Zrušit</Button>
            <Button onClick={handleAddFood} className="bg-orange-600 hover:bg-orange-700">Přidat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Process Entry Dialog */}
      <Dialog open={processDialogOpen} onOpenChange={setProcessDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Zaznamenat: {selectedFieldType?.name}</DialogTitle>
            <DialogDescription>{selectedFieldType?.description || 'Zadejte hodnotu'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedFieldType?.field_type === 'time' && (
              <div className="space-y-2">
                <Label>Čas</Label>
                <Input
                  type="time"
                  value={processForm.value}
                  onChange={(e) => setProcessForm({ ...processForm, value: e.target.value })}
                />
              </div>
            )}
            {selectedFieldType?.field_type === 'temperature' && (
              <div className="space-y-2">
                <Label>Teplota (°C)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={processForm.value}
                  onChange={(e) => setProcessForm({ ...processForm, value: e.target.value })}
                  placeholder="např. 121.5"
                />
              </div>
            )}
            {selectedFieldType?.field_type === 'checkbox' && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="process_checkbox"
                  checked={processForm.value}
                  onCheckedChange={(checked) => setProcessForm({ ...processForm, value: checked })}
                />
                <Label htmlFor="process_checkbox" className="cursor-pointer">
                  {selectedFieldType?.name}
                </Label>
              </div>
            )}
            {selectedFieldType?.field_type === 'text' && (
              <div className="space-y-2">
                <Label>Text</Label>
                <Input
                  value={processForm.value}
                  onChange={(e) => setProcessForm({ ...processForm, value: e.target.value })}
                  placeholder="Zadejte text..."
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProcessDialogOpen(false)}>Zrušit</Button>
            <Button onClick={handleAddProcessEntry} className="bg-blue-600 hover:bg-blue-700">Zaznamenat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Food Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Odebrat jídlo?</AlertDialogTitle>
            <AlertDialogDescription>
              Opravdu chcete odebrat jídlo "{itemToDelete?.food_name}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zrušit</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteFood} className="bg-red-600 hover:bg-red-700">
              Odebrat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default AutoclaveDetailPage;
